'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { cloneElement, useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { OBSIDIAN } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import UIButton from 'UIComponents/button/UIButton';
import UIButtonGroup from 'UIComponents/button/UIButtonGroup';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIDeviceFrame from 'UIComponents/device/UIDeviceFrame';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIModal from 'UIComponents/dialog/UIModal';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIBox from 'UIComponents/layout/UIBox';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIOverhang from 'UIComponents/scroll/UIOverhang';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { useFocusCloseButton } from 'ecosystem-marketplace-core/hooks/useFocusCloseButton';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import MediaQuery from 'ecosystem-marketplace-core/components/layout/MediaQuery';
import FullscreenModalHeader from './FullscreenModalHeader';
import FullscreenModalHeaderGroup from './FullscreenModalHeaderGroup';
import FullscreenModalHeading from './FullscreenModalHeading';
import ThemeShareButton from './ThemeShareButton';
const SUBSCREEN = 'preview modal';
const DeviceFrameBox = styled(UIBox).attrs(props => ({
  alignSelf: props.device === 'desktop' ? 'stretch' : null,
  basis: '100%',
  grow: 1,
  shrink: 1
})).withConfig({
  displayName: "ThemeDemoModal__DeviceFrameBox",
  componentId: "uvgobk-0"
})(["width:", ";overflow:auto;"], props => {
  switch (props.device) {
    case 'tablet':
      return '768px';

    case 'mobile':
      return '375px';

    default:
      return '100%';
  }
});

const ThemeDemoModal = ({
  exampleUrl,
  name,
  onClose,
  onTrackInteraction,
  onTrackPageview,
  providerName,
  primaryButton,
  showShareButton = true
}) => {
  const [activeDevice, setActiveDevice] = useState('desktop');
  const [focused, setFocused] = useState(false);
  const demoModalBackButtonRef = useRef(null);
  const {
    isMobileView,
    isMobileViewHeader
  } = useResponsiveProps({
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.LG]: false
    },
    isMobileViewHeader: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.MD]: false
    }
  });
  const backButtonClassName = isMobileViewHeader ? 'p-top-2' : "";
  const deviceFrameBoxClassName = isMobileView ? 'p-top-6' : "";
  const titleClassName = isMobileViewHeader ? 'p-top-4' : "";
  const primaryButtonClassName = classNames('m-top-4', primaryButton.props.className);
  const handleTrackInteraction = useCallback((action, eventProperties = {}) => {
    onTrackInteraction(action, Object.assign({}, eventProperties, {
      subscreen: SUBSCREEN
    }));
  }, [onTrackInteraction]);
  const renderDeviceButton = useCallback(device => {
    return /*#__PURE__*/_jsxs(UIButton, {
      active: activeDevice === device,
      onClick: () => {
        setActiveDevice(device);
        handleTrackInteraction('set preview device', {
          value: device
        });
      },
      size: "small",
      use: "tertiary-light",
      children: [/*#__PURE__*/_jsx(UIIcon, {
        name: device
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: `marketplace-assets-ui.themes.details.demoModal.${device}`
      })]
    });
  }, [activeDevice, handleTrackInteraction]);
  useEffect(() => {
    onTrackPageview({
      subscreen: SUBSCREEN
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isMobileView) {
      setActiveDevice('desktop');
    }
  }, [isMobileView]);
  useEffect(() => {
    if (demoModalBackButtonRef) {
      setFocused(demoModalBackButtonRef.current != null);
    }
  }, []);
  useFocusCloseButton(focused, demoModalBackButtonRef.current);
  return /*#__PURE__*/_jsxs(UIModal, {
    use: "fullscreen",
    children: [/*#__PURE__*/_jsxs(FullscreenModalHeader, {
      isMobileView: isMobileViewHeader,
      children: [/*#__PURE__*/_jsx(FullscreenModalHeaderGroup, {
        children: /*#__PURE__*/_jsxs(UIButton, {
          "aria-label": unescapedText('marketplace-assets-ui.themes.details.screenshots.modal.backButton'),
          buttonRef: demoModalBackButtonRef,
          onClick: evt => {
            onClose(evt);
            onTrackInteraction('close preview modal');
          },
          use: "link",
          children: [/*#__PURE__*/_jsx(UIIcon, {
            className: backButtonClassName,
            name: "left",
            size: isMobileViewHeader ? 'xs' : 'xxs'
          }), /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: ScreenSizes.MD,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-assets-ui.themes.details.screenshots.modal.backButton"
            })
          })]
        })
      }), /*#__PURE__*/_jsxs(FullscreenModalHeaderGroup, {
        className: titleClassName,
        middle: true,
        children: [/*#__PURE__*/_jsx(FullscreenModalHeading, {
          subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.details.demoModal.providerName",
            options: {
              providerName
            }
          }),
          title: name
        }), /*#__PURE__*/_jsx(MediaQuery, {
          maxScreenSize: ScreenSizes.MD,
          children: /*#__PURE__*/cloneElement(primaryButton, {
            className: primaryButtonClassName
          })
        })]
      }), /*#__PURE__*/_jsx(FullscreenModalHeaderGroup, {
        children: /*#__PURE__*/_jsxs(UIButtonWrapper, {
          justify: "end",
          children: [showShareButton && /*#__PURE__*/_jsx(ThemeShareButton, {
            "aria-label": unescapedText('marketplace-assets-ui.themes.details.screenshots.modal.shareButton'),
            responsive: true,
            use: "transparent"
          }), /*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: ScreenSizes.MD,
            children: primaryButton
          })]
        })
      })]
    }), /*#__PURE__*/_jsxs(UIDialogBody, {
      className: "p-top-0",
      style: {
        position: 'relative'
      },
      children: [/*#__PURE__*/_jsxs(UIFlex, {
        direction: "column",
        align: "center",
        style: {
          height: '100%'
        },
        children: [/*#__PURE__*/_jsx(MediaQuery, {
          minScreenSize: ScreenSizes.LG,
          children: /*#__PURE__*/_jsxs(UIGrid, {
            className: "align-center",
            style: {
              width: '100%'
            },
            children: [/*#__PURE__*/_jsx(UIGridItem, {
              size: 3
            }), /*#__PURE__*/_jsx(UIGridItem, {
              className: "text-center",
              size: 6,
              children: /*#__PURE__*/_jsx(UIBox, {
                basis: 0,
                className: "p-y-5",
                grow: 1,
                children: /*#__PURE__*/_jsxs(UIButtonGroup, {
                  children: [renderDeviceButton('desktop'), renderDeviceButton('tablet'), renderDeviceButton('mobile')]
                })
              })
            }), /*#__PURE__*/_jsx(UIGridItem, {
              className: "text-right",
              size: 3,
              children: /*#__PURE__*/_jsx(UIButton, {
                external: true,
                href: exampleUrl,
                onClick: () => onTrackInteraction('click preview link'),
                use: "link",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "marketplace-assets-ui.themes.details.demoModal.externalLink"
                })
              })
            })]
          })
        }), /*#__PURE__*/_jsx(DeviceFrameBox, {
          className: deviceFrameBoxClassName,
          device: activeDevice,
          children: /*#__PURE__*/_jsx(UIDeviceFrame, {
            height: activeDevice === 'desktop' ? '100%' : null,
            type: activeDevice,
            children: /*#__PURE__*/_jsx("iframe", {
              "aria-label": unescapedText('marketplace-assets-ui.themes.details.demoModal.iframeAriaLabel', {
                name
              }),
              frameBorder: 0,
              height: "100%",
              scrolling: "yes",
              src: exampleUrl,
              style: {
                display: 'block'
              },
              title: unescapedText('marketplace-assets-ui.themes.details.demoModal.iframeTitle', {
                name
              }),
              width: "100%"
            })
          })
        })]
      }), /*#__PURE__*/_jsx(UIOverhang, {
        side: "top",
        gradientColor: OBSIDIAN,
        opacity: 0.1,
        size: 4
      })]
    })]
  });
};

ThemeDemoModal.propTypes = {
  exampleUrl: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onClose: PropTypes.func,
  onTrackInteraction: PropTypes.func.isRequired,
  onTrackPageview: PropTypes.func.isRequired,
  primaryButton: PropTypes.node.isRequired,
  providerName: PropTypes.string.isRequired,
  showShareButton: PropTypes.bool
};
export default ThemeDemoModal;